/**
 * Zentrale Text-Datei für das Frontend.
 *
 * Hier kannst du sämtliche im UI sichtbaren Texte pflegen.
 * Struktur ist nach Komponenten/Bereichen sortiert.
 *
 * Hinweise:
 * - {name}, {number}, ... sind Platzhalter (werden zur Laufzeit eingesetzt)
 * - Emojis können beliebig geändert/entfernt werden
 * - Die Numerologie-Bedeutungen für Zahlen 1-9 stehen unter `numerology.meanings`
 */

export const texts = {
  app: {
    brandTitle: '✨ Zahlio',
    brandSubtitle: 'by Anita',
    loading: '✨ Laden...',
  },

  profileSetup: {
    introLine1: 'Hinter deinem Geburtstag verbirgt sich eine spannende Zahlenwelt.',
    introLine2: 'Entdecke, welche Themen dich durchs Leben begleiten.',
    formTitle: 'Dein Profil erstellen',
    nameLabel: '👤 Dein Name',
    namePlaceholder: 'Gib deinen vollständigen Geburtsnamen ein',
    dateLabel: '📅 Geburtsdatum',
    submitButton: '✨ Profil erstellen',
    submittingButton: '⏳ Wird erstellt...',
    storageHint: 'Dein Profil wird sicher in diesem Browser gespeichert',
    footerHint: '✨ Numerologie zur Inspiration und Unterhaltung.',
  },

  mainView: {
    tabHoroscope: '📅 Tagesnumeroskop',
    tabProfile: '🪞 Mein Profil',
    footerButtonWebsite: 'Mehr über Anitas Numerologie',
    footerButtonTelegram: '📩 Anfrage an Anita per Telegram',
    footerOffer: 'Ein Angebot von anitafurrer.ch · Numerologie im Alltag',
    footerImpulse: 'Die Zahlen sind Impulse für deinen Weg. Die Schwingung entfaltet ihre Wirkung in dir.',
  },

  profileView: {
    pentagonLabelLifePath: 'Lebenszahl',
    pentagonLabelExpression: 'Ausdruck',
    pentagonLabelBirthday: 'Geburtstag',
    pentagonLabelSoul: 'Seele',
    pentagonLabelDestiny: 'Inneres Selbst',
    overlayLabelLifePath: 'Lebenszahl',
    overlayLabelExpression: 'Ausdrucks-Zahl',
    overlayLabelBirthday: 'Geburtstags-Zahl',
    overlayLabelSoul: 'Seelen-Zahl',
    overlayLabelDestiny: 'Inneres Selbst-Zahl',
    sectionCharacteristics: '✨ Charakteristiken',
    sectionStrengths: '💪 Stärken',
    sectionChallenges: '⚡ Herausforderungen',
    sectionSpiritual: '🙏 Zahlenimpuls',
    sectionColors: '🎨 Farben',
    closeButton: 'Schliessen',
    avatarPickerTitle: 'Avatar wählen',
    discoverHint: '✨ Tippe auf deine Zahlen und deinen Avatar, um mehr zu entdecken.',
    anitaUpdatesTitle: '✉️ Infos von Anita',
    anitaUpdatesDescription: 'Ich möchte weiterführende Infos und Veranstaltungshinweise von Anita via Zahlio erhalten.',
    anitaUpdatesUnavailableHint: 'Ausserhalb von Telegram wird die Auswahl nur lokal vorgemerkt.',
    editProfileButton: '✏️ Profil bearbeiten',
  },

  profileEditForm: {
    title: 'Profil bearbeiten',
    subtitle: 'Aktualisiere deine Daten',
    nameLabel: '👤 Dein Name',
    dateLabel: '📅 Geburtsdatum',
    cancelButton: 'Abbrechen',
    saveButton: '✓ Speichern',
    savingButton: '⏳ Speichert...',
    helperText: 'Deine Zahlen werden neu berechnet',
  },

  dailyHoroscope: {
    todayLabel: '📅 Heute',
    personalDayLabel: 'Dein persönlicher Tag',
    universalDayLabel: '🌍 Kollektiver Tag',
    universalDayHint: '· kollektive Tages-Energie',
    compatibilityHeading: 'Wie passt das zu dir?',
    compatibilityHarmonyEmoji: '🎯',
    compatibilityHarmonyStatus: 'harmonisch',
    compatibilityHarmonySentence: 'harmoniert wunderbar mit deiner Lebenszahl {lifePath}',
    compatibilitySupportEmoji: '✨',
    compatibilitySupportStatus: 'unterstützend',
    compatibilitySupportSentence: 'ergänzt deine Lebenszahl {lifePath} auf stärkende Weise',
    compatibilityChallengeEmoji: '⚡',
    compatibilityChallengeStatus: 'herausfordernd',
    compatibilityChallengeSentence: 'bringt deiner Lebenszahl {lifePath} neue Perspektiven',
    dailyConstellationSentence: 'Heute ist ein **{dailyNumber}-Tag** - er {compatSentence}.',
    personalConstellationSentence: '**{name}**, deine Lebenszahl begleitet dich mit dem Thema*{lifePathMeaning}* und bildet zusammen mit der heutigen Energie eine {statusAdjective} Konstellation.',
    universalWeekTitle: '🌐 Wochenzahl kollektiv',
    universalWeekSentence: 'Die kollektive Wochenzahl steht aktuell auf **{weekNumber}** ({weekMeaning}).',
    universalWeekExplanation: 'Sie beschreibt die Grundqualität der laufenden Woche und den kollektiven Wochenrhythmus, unabhängig von deinem persönlichen Profil.',
    personalWeekTitle: '📆 Deine Zahl im Wochenfeld',
    personalWeekSentence: 'Für dich wirkt diese Woche zusätzlich über die persönliche Wochenzahl **{weekNumber}** ({weekMeaning}).',
    personalWeekExplanation: 'Die allgemeine Wochenqualität von *{universalWeekMeaning}* trifft bei dir auf eine persönliche Prägung von *{personalWeekMeaning}*. Deine Tages-Zahl **{dailyNumber}** entfaltet sich also nicht isoliert, sondern innerhalb dieses persönlichen Wochenrahmens.',
    moonPhaseTitle: 'Mondgeflüster',
    moonPhaseSentence: 'Aktuell wirkt die Mondphase **{phaseName}**.',
    characteristicsTitle: '✨ Charakteristiken des Tages',
  },

  moonPhases: {
    newMoon: {
      name: 'Neumond - Potential, Träume',
      emoji: '🌑',
      energy: [
        'Zeit für Rückzug, innere Sammlung und neue Absichten.',
        'Ein guter Moment, um still zu werden und neue Vorsätze zu fassen.',
        'Der Neumond lädt ein, nach innen zu gehen und Saat für Neues zu legen.',
      ],
    },
    waxingCrescent: {
      name: 'Zunehmende Sichel - Mut, Fortschritt, Vertrauen',
      emoji: '🌒',
      energy: [
        'Zarte Aufbruchskraft, erste Schritte und bewusstes Ausrichten.',
        'Die ersten zarten Schritte zählen jetzt — richte dich bewusst aus.',
        'Eine feine Aufbruchsenergie wächst. Beginne behutsam, was du dir vorgenommen hast.',
      ],
    },
    firstQuarter: {
      name: 'Zunehmender Halbmond - Selbstvertrauen, Hingabe',
      emoji: '🌓',
      energy: [
        'Aktive Umsetzungsenergie, Entscheidungen und Momentum.',
        'Jetzt ist Tatkraft gefragt — triff Entscheidungen und bleib dran.',
        'Der Mond gibt Schub. Ein guter Moment, um Hindernisse anzugehen.',
      ],
    },
    waxingGibbous: {
      name: 'Zunehmender Dreiviertel Mond - Optimierung, Anpassung',
      emoji: '🌔',
      energy: [
        'Wachstum, Aufbau und Fokus auf das, was sich entfalten soll.',
        'Dranbleiben lohnt sich jetzt. Schenk dem Aufmerksamkeit, das wachsen soll.',
        'Die Energie nimmt zu — feile an den Details und bleib fokussiert.',
      ],
    },
    fullMoon: {
      name: 'Vollmond - Ergebnisse, Dankbarkeit',
      emoji: '🌕',
      energy: [
        'Sichtbarkeit, Intensität und emotionale Fülle.',
        'Alles tritt jetzt klarer hervor — Gefühle dürfen sichtbar sein.',
        'Der Vollmond bringt Fülle und Klarheit. Ein guter Moment, innezuhalten und wahrzunehmen.',
      ],
    },
    waningGibbous: {
      name: 'Abnehmender Dreiviertelmond - Entspannung, Neuformierung',
      emoji: '🌖',
      energy: [
        'Einordnen, Ernten und bewusstes Nachspüren.',
        'Zeit, das Erreichte zu würdigen und in Ruhe nachzuspüren.',
        'Der Mond nimmt ab — ernte, ordne ein und teile, was du gewonnen hast.',
      ],
    },
    lastQuarter: {
      name: 'Abnehmender Halbmond - Neubewertung, Ausgleich, Vertrauen',
      emoji: '🌗',
      energy: [
        'Loslassen, Klären und konsequentes Vereinfachen.',
        'Ein guter Moment, um Ballast abzulegen und Dinge zu klären.',
        'Vereinfache jetzt bewusst — was darf gehen, damit es leichter wird?',
      ],
    },
    waningCrescent: {
      name: 'Abnehmende Mondsichel - Heilung, Beruhigung',
      emoji: '🌘',
      energy: [
        'Abschluss, Regeneration und Vorbereitung auf einen neuen Zyklus.',
        'Zeit zum Ausruhen und Auftanken, bevor ein neuer Zyklus beginnt.',
        'Der Mond verabschiedet sich — komm zur Ruhe und lass den Kreis sich schliessen.',
      ],
    },
  },

  notificationSettings: {
    title: '🔔 Tägliches Push-Numeroskop',
    description: 'Erhalte jeden Morgen deine persönliche Tages-Zahl direkt in Telegram.',
    hourLabel: 'Uhrzeit:',
    localTimeHint: 'deine Ortszeit',
    unavailableHint: 'Nur in Telegram verfügbar.',
  },

  errors: {
    unknown: 'Unbekannter Fehler',
    pushNotConfigured: 'Push-API ist nicht konfiguriert (VITE_PUSH_API_URL fehlt)',
    pushTelegramOnly: 'Push-Benachrichtigungen sind nur in Telegram verfügbar',
    apiErrorPrefix: 'API-Fehler: ',
  },

  numerology: {
    meanings: {
      1: {
        meaning: 'Führung & Eigenständigkeit',
        characteristics: ['Ehrgeizig', 'Mutig', 'Entschlossen', 'Wegweisend', 'Zielgerichtet'],
        description: `Die 1 steht für den Anfang. Sie bringt Bewegung in die Dinge und hat den Mut, neue Wege zu gehen. Menschen mit einer starken 1 übernehmen gerne Verantwortung, treffen Entscheidungen und warten selten darauf, dass andere den ersten Schritt machen.

Die 1 liebt Eigenständigkeit und vertraut oft lieber auf die eigene Erfahrung als auf fremde Meinungen. Sie möchte gestalten, etwas bewegen und ihre Spuren hinterlassen.`,
        strengths: [
          'Initiative ergreifen',
          'Entscheidungen treffen',
          'Verantwortung übernehmen',
          'Neue Wege erkennen',
          'Andere inspirieren',
          'Herausforderungen anpacken',
        ],
        challenges: [
          'Alles alleine machen wollen',
          'Ungeduldig werden',
          'Hilfe nur schwer annehmen',
          'Zu viel Druck auf sich selbst ausüben',
          'Dominant wirken, ohne es zu beabsichtigen',
          'Sich von anderen abgrenzen',
        ],
        spiritual: `Die 1 erinnert daran, dass jeder Weg mit einem ersten Schritt beginnt. Nicht alles muss perfekt geplant sein. Oft zeigt sich der Weg erst, wenn man losgeht.

Mut bedeutet nicht, keine Angst zu haben. Mut bedeutet, trotzdem anzufangen.`,
        colors: ['Rot', 'Gold'],
      },
      2: {
        meaning: 'Verbindung & Feingefühl',
        characteristics: ['Einfühlsam', 'Intuitiv', 'Sensibel', 'Achtsam', 'Verbindend'],
        description: `Die 2 steht für Verbindung. Sie nimmt feine Zwischentöne wahr, erkennt Stimmungen oft schneller als Worte und besitzt ein natürliches Gespür für Menschen.

Während die 1 gerne vorangeht, beobachtet die 2 zunächst aufmerksam, was um sie herum geschieht. Sie baut Brücken, schafft Vertrauen und bringt unterschiedliche Sichtweisen zusammen.

Die 2 weiss, dass nicht jede Herausforderung mit Druck gelöst werden muss. Oft erreicht sie mit Feingefühl mehr als andere mit Lautstärke.`,
        strengths: [
          'Gut zuhören',
          'Menschen verbinden',
          'Konflikte entschärfen',
          'Vertrauen schaffen',
          'Teamgeist fördern',
          'Feinheiten wahrnehmen',
        ],
        challenges: [
          'Zu sehr auf andere achten',
          'Entscheidungen hinauszögern',
          'Konflikten aus dem Weg gehen',
          'Sich anpassen, um dazuzugehören',
          'Die eigenen Bedürfnisse übersehen',
          'Unsicherheit bei wichtigen Entscheidungen',
        ],
        spiritual: `Die 2 erinnert daran, dass wahre Stärke nicht immer laut auftritt.

Wer aufmerksam zuhört, erkennt oft Zusammenhänge, die anderen verborgen bleiben. Gleichzeitig darf die 2 lernen, den eigenen Bedürfnissen genauso viel Aufmerksamkeit zu schenken wie denen anderer.`,
        colors: ['Silber', 'Hellblau'],
      },
      3: {
        meaning: 'Kreativität & Ausdruck',
        characteristics: ['Kreativ', 'Ausdrucksstark', 'Kommunikativ', 'Inspirierend', 'Lebensfroh'],
        description: `Die 3 steht für Ausdruck, Kreativität und die Freude, etwas mit anderen zu teilen. Sie bringt Ideen in Bewegung, findet Worte für Gedanken und verleiht dem Leben Farbe.

Menschen mit einer starken 3 verfügen oft über die Fähigkeit, andere zu begeistern, Geschichten zu erzählen oder neue Perspektiven sichtbar zu machen. Die 3 liebt den Austausch und fühlt sich wohl, wenn sie ihre Gedanken, Gefühle oder Talente zum Ausdruck bringen kann.

Wo die 3 wirkt, entsteht Lebendigkeit, Inspiration und die Bereitschaft, Neues auszuprobieren.`,
        strengths: [
          'Kreative Lösungen finden',
          'Gedanken verständlich ausdrücken',
          'Andere inspirieren',
          'Offen auf Menschen zugehen',
          'Freude und Leichtigkeit vermitteln',
          'Neue Ideen entwickeln',
        ],
        challenges: [
          'Sich verzetteln',
          'Zu viele Ideen gleichzeitig verfolgen',
          'Unruhe entwickeln',
          'Angefangenes nicht abschliessen',
          'Oberflächlich wirken',
          'Kritik persönlich nehmen',
        ],
        spiritual: `Die 3 erinnert daran, dass Ideen erst dann ihre Wirkung entfalten, wenn sie geteilt werden.

Nicht jeder Gedanke muss perfekt sein. Oft entsteht etwas Wertvolles genau dann, wenn man den Mut hat, sich zu zeigen und den ersten Entwurf in die Welt zu bringen.`,
        colors: ['Gelb', 'Orange'],
      },
      4: {
        meaning: 'Stabilität & Struktur',
        characteristics: ['Zuverlässig', 'Strukturiert', 'Bodenständig', 'Organisiert', 'Beständig'],
        description: `Die 4 steht für Struktur, Stabilität und ein solides Fundament. Sie bringt Ordnung in das, was wachsen soll, und schafft die Voraussetzungen dafür, dass Ideen Bestand haben.

Menschen mit einer starken 4 schätzen Verlässlichkeit, klare Abläufe und nachvollziehbare Lösungen. Sie besitzen oft die Fähigkeit, Schritt für Schritt vorzugehen und auch dann dranzubleiben, wenn andere längst das Interesse verloren haben.

Die 4 erinnert daran, dass grosse Vorhaben selten über Nacht entstehen. Was Bestand haben soll, braucht Zeit, Geduld und ein stabiles Fundament.`,
        strengths: [
          'Ordnung schaffen',
          'Verantwortung übernehmen',
          'Dranbleiben',
          'Strukturiert planen',
          'Verlässlichkeit ausstrahlen',
          'Praktische Lösungen finden',
        ],
        challenges: [
          'Zu starr werden',
          'Veränderungen schwer annehmen',
          'Kontrollbedürfnis entwickeln',
          'Sich selbst unter Druck setzen',
          'Zu kritisch mit sich und anderen sein',
          'Sicherheit über alles stellen',
        ],
        spiritual: `Die 4 erinnert daran, dass Beständigkeit oft mehr bewirkt als ein kurzer Energieschub.

Nicht jeder Fortschritt ist sofort sichtbar. Manchmal entsteht etwas Wertvolles genau durch die kleinen Schritte, die jeden Tag aufs Neue gegangen werden.`,
        colors: ['Dunkelgrün', 'Braun'],
      },
      5: {
        meaning: 'Freiheit & Veränderung',
        characteristics: [
          'Freiheitsliebend',
          'Abenteuerlustig',
          'Anpassungsfähig',
          'Entdeckungsfreudig',
          'Vielseitig',
        ],
        description: `Die 5 steht für Freiheit, Veränderung und Lebenserfahrung. Sie möchte die Welt entdecken, Horizonte erweitern und eigene Erfahrungen sammeln.

Menschen mit einer starken 5 fühlen sich oft wohl, wenn sie Möglichkeiten haben, Neues kennenzulernen und ihren eigenen Weg zu gestalten. Sie schätzen Abwechslung und lassen sich ungern in feste Schubladen stecken.

Die 5 erinnert daran, dass Entwicklung häufig dort entsteht, wo man bereit ist, Bekanntes hinter sich zu lassen und neue Erfahrungen zuzulassen.`,
        strengths: [
          'Flexibel bleiben',
          'Chancen erkennen',
          'Schnell auf Neues eingehen',
          'Erfahrungen Sammeln',
          'Schnell lernen',
          'Veränderungen anstossen',
        ],
        challenges: [
          'Sich schwer festlegen',
          'Entscheidungen ständig hinterfragen',
          'Schnell das Interesse verlieren',
          'Unruhe entwickeln',
          'Zu viel auf einmal wollen',
          'Grenzen ignorieren',
        ],
        spiritual: `Die 5 erinnert daran, dass Wachstum oft ausserhalb der gewohnten Wege entsteht.

Neue Erfahrungen erweitern den Horizont. Gleichzeitig darf Freiheit nicht mit Beliebigkeit verwechselt werden. Auch die grösste Entdeckungsreise braucht manchmal eine Richtung.`,
        colors: ['Hellgrün', 'Türkis'],
      },
      6: {
        meaning: 'Liebe & Fürsorge',
        characteristics: [
          'Fürsorglich',
          'Verantwortungsbewusst',
          'Hilfsbereit',
          'Loyal',
          'Harmonieorientiert',
        ],
        description: `Die 6 steht für Verantwortung, Fürsorge und Verbundenheit. Sie hat ein feines Gespür dafür, was Menschen brauchen, und übernimmt oft ganz selbstverständlich Verantwortung für ihr Umfeld.

Menschen mit einer starken 6 legen Wert auf Vertrauen, Zusammenhalt und ein harmonisches Miteinander. Sie kümmern sich gerne um andere, schaffen Geborgenheit und tragen dazu bei, dass sich Menschen wohlfühlen.

Die 6 erinnert daran, dass echte Stärke oft in den kleinen Gesten des Alltags sichtbar wird.`,
        strengths: [
          'Verantwortung übernehmen',
          'Für andere da sein',
          'Vertrauen schaffen',
          'Harmonie fördern',
          'Verbindlichkeit leben',
          'Menschen unterstützen',
        ],
        challenges: [
          'Zu viel Verantwortung tragen',
          'Die eigenen Bedürfnisse zurückstellen',
          'Sich für alles zuständig fühlen',
          'Schwierigkeiten haben, loszulassen',
          'Hohe Erwartungen an sich selbst stellen',
          'Sich durch die Sorgen anderer belasten lassen',
        ],
        spiritual: `Die 6 erinnert daran, dass Fürsorge in beide Richtungen fliessen darf.

Wer sich um andere kümmert, darf auch auf die eigenen Bedürfnisse achten. Verantwortung bedeutet nicht, alles alleine tragen zu müssen.`,
        colors: ['Rosa', 'Grün'],
      },
      7: {
        meaning: 'Erkentnis & Vertrauen',
        characteristics: [
          'Analytisch',
          'Wissbegierig',
          'Reflektiert',
          'Tiefgründig',
          'Wahrnehmungsstark',
        ],
        description: `Die 7 steht für Erkenntnis, Verständnis und die Suche nach dem, was hinter den Dingen liegt. Sie möchte Zusammenhänge erkennen, Fragen stellen und das Leben nicht nur oberflächlich betrachten.

Menschen mit einer starken 7 beschäftigen sich oft intensiv mit Themen, die sie interessieren. Sie beobachten genau, denken gerne nach und möchten verstehen, wie etwas wirklich funktioniert. Dabei vertrauen sie lieber eigenen Erkenntnissen als schnellen Antworten.

Die 7 erinnert daran, dass manche Einsichten Zeit brauchen und nicht alles sofort erklärt werden kann.`,
        strengths: [
          'Zusammenhänge erkennen',
          'Gründlich analysieren',
          'Wissen vertiefen',
          'Gute Beobachtungsgabe',
          'Eigenständig denken',
          'Vertrauen in die innere Stimme entwickeln',
        ],
        challenges: [
          'Zu viel nachdenken',
          'Sich zurückziehen',
          'Zweifel entwickeln',
          'Perfekte Antworten suchen',
          'Sich schwer öffnen',
          'Abstand statt Nähe wählen',
        ],
        spiritual: `Die 7 erinnert daran, dass nicht jede Frage sofort beantwortet werden muss.

Manche Erkenntnisse entstehen durch Beobachtung, Erfahrung und Geduld. Vertrauen wächst oft dann, wenn man nicht mehr jede Antwort erzwingen möchte.`,
        colors: ['Violett', 'Dunkelblau'],
      },
      8: {
        meaning: 'Erfolg & Gestaltungskraft',
        characteristics: [
          'Zielstrebig',
          'Tatkräftig',
          'Verantwortungsbewusst',
          'Durchsetzungsstark',
          'Gestaltend',
        ],
        description: `Die 8 steht für Erfolg, Gestaltungskraft und die Fähigkeit, Ideen in die Wirklichkeit zu bringen. Sie möchte etwas aufbauen, Verantwortung übernehmen und sichtbare Ergebnisse schaffen.

Menschen mit einer starken 8 verfügen oft über einen ausgeprägten Sinn für Möglichkeiten, Ressourcen und Umsetzung. Sie erkennen, was machbar ist, und besitzen die Fähigkeit, Vorhaben Schritt für Schritt zu verwirklichen.

Die 8 erinnert daran, dass Erfolg selten Zufall ist. Er entsteht durch Entscheidungen, Ausdauer und die Bereitschaft, Verantwortung für das eigene Handeln zu übernehmen.`,
        strengths: [
          'Ziele verfolgen',
          'Verantwortung übernehmen',
          'Projekte umsetzen',
          'Strategisch denken',
          'Chancen erkennen',
          'Ausdauer beweisen',
        ],
        challenges: [
          'Zu viel kontrollieren wollen',
          'Sich über Leistung definieren',
          'Ungeduldig werden',
          'Alles selbst steuern wollen',
          'Hohe Erwartungen an sich und andere stellen',
          'Die Balance zwischen Beruf und Privatleben verlieren',
        ],
        spiritual: `Die 8 erinnert daran, dass echter Erfolg nicht nur daran gemessen wird, was erreicht wird, sondern auch daran, wie der Weg dorthin gestaltet wird.

Wer Verantwortung übernimmt, darf sich gleichzeitig erlauben, Unterstützung anzunehmen und Vertrauen zu entwickeln.`,
        colors: ['Gold', 'Dunkelgrün'],
      },
      9: {
        meaning: 'Vollendung & Mitgefühl',
        characteristics: ['Mitfühlend', 'Grosszügig', 'Verständnisvoll', 'Weitsichtig', 'Idealistisch'],
        description: `Die 9 steht für Mitgefühl, Menschlichkeit und Vollendung. Sie trägt die Erfahrungen aller Zahlen in sich und blickt oft über die eigenen Bedürfnisse hinaus auf das grössere Ganze.

Menschen mit einer starken 9 besitzen häufig ein ausgeprägtes Verständnis für andere Menschen und deren Lebenswege. Sie erkennen Zusammenhänge, zeigen Mitgefühl und wünschen sich eine Welt, in der Fairness, Verständnis und gegenseitige Unterstützung einen Platz haben.

Die 9 erinnert daran, dass jeder Lebensabschnitt seine Zeit hat. Manches darf wachsen, manches darf gehen und manches darf in Frieden abgeschlossen werden.`,
        strengths: [
          'Mitgefühl zeigen',
          'Andere verstehen',
          'Über den eigenen Horizont hinausblicken',
          'Menschen unterstützen',
          'Erfahrungen weitergeben',
          'Veränderungen begleiten',
        ],
        challenges: [
          'Die Probleme anderer zu sehr übernehmen',
          'Sich selbst vergessen',
          'Enttäuschungen lange mittragen',
          'Schwierigkeit beim Loslassen',
          'Zu hohe Erwartungen an Menschen haben',
          'Sich für alles verantwortlich fühlen',
        ],
        spiritual: `Die 9 erinnert daran, dass Mitgefühl nicht bedeutet, alles tragen zu müssen.

Wer anderen Verständnis entgegenbringt, darf sich selbst mit derselben Freundlichkeit begegnen. Loslassen bedeutet nicht aufzugeben, sondern Raum für Neues entstehen zu lassen.`,
        colors: ['Violett', 'Purpur'],
      },
    },
  },
} as const

export type NumerologyMeaningKey = keyof typeof texts.numerology.meanings
export type MoonPhaseKey = keyof typeof texts.moonPhases
